import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowLeft, Check, Eye, EyeSlash, Warning } from "@phosphor-icons/react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

const ConnectResetPassword = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  if (!token) {
    return (
      <main className="min-h-screen bg-[#EDEBE4] flex items-center justify-center px-6">
        <div className="text-center max-w-md">
          <Warning size={48} weight="light" className="text-[#1A1A1A]/40 mx-auto mb-4" />
          <h1 className="font-heading text-xl text-[#1A1A1A] mb-2">Lien invalide</h1>
          <p className="text-[#1A1A1A]/60 text-sm mb-6">Ce lien de réinitialisation est invalide ou a expiré.</p>
          <Link to="/connect/forgot-password" className="btn-dark inline-flex items-center gap-3">
            Nouvelle demande <ArrowRight size={16} />
          </Link>
        </div>
      </main>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 8) { setError("Le mot de passe doit contenir au moins 8 caractères"); return; }
    if (password !== confirm) { setError("Les mots de passe ne correspondent pas"); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/api/connect/auth/reset-password`, { token, new_password: password });
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.detail || "Erreur lors de la réinitialisation");
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <main className="min-h-screen bg-[#EDEBE4] flex items-center justify-center px-6" data-testid="reset-success">
        <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="text-center max-w-md">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <Check size={32} weight="bold" className="text-green-600" />
          </div>
          <h1 className="font-heading text-2xl text-[#1A1A1A] mb-4">Mot de passe modifié</h1>
          <p className="text-[#1A1A1A]/70 font-light mb-8">
            Votre mot de passe a été mis à jour avec succès. Vous pouvez maintenant vous connecter.
          </p>
          <Link to="/connect" className="btn-dark inline-flex items-center gap-3" data-testid="go-to-login">
            Se connecter <ArrowRight size={16} weight="bold" />
          </Link>
        </motion.div>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-[#EDEBE4] flex items-center justify-center px-6" data-testid="reset-password-page">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md">
        <div className="text-center mb-10">
          <Link to="/" className="inline-block mb-6">
            <span className="font-heading text-2xl tracking-[0.35em] text-[#1A1A1A] uppercase">Altura</span>
          </Link>
          <h1 className="font-heading text-3xl text-[#1A1A1A] mb-2">Nouveau mot de passe</h1>
          <p className="text-[#1A1A1A]/60 text-sm font-light">Choisissez un nouveau mot de passe pour votre compte</p>
        </div>

        <div className="bg-white border border-[#1A1A1A]/10 p-8">
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="text-[#1A1A1A]/70 text-xs tracking-wide uppercase block mb-2">Nouveau mot de passe</label>
              <div className="relative">
                <input type={showPw ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} required minLength={8}
                  className="input-luxury w-full bg-transparent pr-10" placeholder="8 caractères minimum" data-testid="reset-password-input" />
                <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#1A1A1A]/40">
                  {showPw ? <EyeSlash size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
            <div>
              <label className="text-[#1A1A1A]/70 text-xs tracking-wide uppercase block mb-2">Confirmer le mot de passe</label>
              <input type={showPw ? "text" : "password"} value={confirm} onChange={(e) => setConfirm(e.target.value)} required
                className="input-luxury w-full bg-transparent" placeholder="Confirmez votre mot de passe" data-testid="reset-confirm-input" />
            </div>
            {error && <p className="text-red-600 text-sm" data-testid="reset-error">{error}</p>}
            <button type="submit" disabled={loading} className="btn-dark w-full flex items-center justify-center gap-3" data-testid="reset-submit-btn">
              {loading ? "Modification..." : "Modifier mon mot de passe"}
              <ArrowRight size={16} weight="bold" />
            </button>
          </form>
        </div>

        <div className="text-center mt-6">
          <Link to="/connect" className="text-[#1A1A1A]/60 text-sm hover:text-[#1A1A1A] transition-colors">
            <ArrowLeft size={14} className="inline mr-1" /> Retour à la connexion
          </Link>
        </div>
      </motion.div>
    </main>
  );
};

export default ConnectResetPassword;
